// How much of the day the time grid draws.
//
// A fixed 05:00–21:00 spent more than half its height on hours nobody works.
// Drawing the practice's own day instead gives those pixels back — which is
// most of the difference on a phone, where the grid gets about 460px.
//
// Narrowing has one hazard: anything outside the window is invisible. So the
// window covers the appointments on screen too, and only then trims.

const DEFAULT_MIN_HOUR = 5;
const DEFAULT_MAX_HOUR = 21;
// An hour of air after the last working hour, so a late appointment has room
// — and none before the first: the practice asked for 8 to 5, and the 7 AM
// row was empty every day.
const PADDING_AFTER_HOURS = 1;

interface WorkingInterval { start?: string; end?: string; }
export interface SchedulableProvider {
  workingHours?: Record<string, WorkingInterval[] | undefined> | null;
}
export interface WindowedAppointment { start: Date; end: Date; }

const hourOf = (clock?: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(clock || ''));
  if (!match) return null;
  const hour = Number(match[1]) + (Number(match[2]) > 0 ? 1 : 0) - (Number(match[2]) > 0 ? 1 : 0);
  return Number.isFinite(hour) ? Number(match[1]) + Number(match[2]) / 60 : null;
};

const clamp = (hour: number) => Math.max(0, Math.min(24, hour));
const asClock = (hour: number) => `${String(Math.floor(hour)).padStart(2, '0')}:00:00`;

/**
 * The first and last hour the grid should draw, as FullCalendar clock strings.
 *
 * Falls back to the old fixed window when there is nothing to reason about —
 * providers arrive from a fetch, and a grid that collapsed to nothing while
 * that was in flight would be worse than one that is too tall.
 */
export function gridTimeWindow(
  providers: SchedulableProvider[],
  appointments: WindowedAppointment[],
): { slotMinTime: string; slotMaxTime: string } {
  let earliest: number | null = null;
  let latest: number | null = null;

  const note = (from: number | null, to: number | null) => {
    if (from !== null) earliest = earliest === null ? from : Math.min(earliest, from);
    if (to !== null) latest = latest === null ? to : Math.max(latest, to);
  };

  (providers || []).forEach((provider) => {
    Object.values(provider?.workingHours || {}).forEach((intervals) => {
      (intervals || []).forEach((interval) => note(hourOf(interval.start), hourOf(interval.end)));
    });
  });

  if (earliest === null || latest === null) {
    return { slotMinTime: asClock(DEFAULT_MIN_HOUR), slotMaxTime: asClock(DEFAULT_MAX_HOUR) };
  }

  let min = clamp(Math.floor(earliest));
  let max = clamp(Math.ceil(latest) + PADDING_AFTER_HOURS);

  // Anything booked outside those hours still has to be visible.
  (appointments || []).forEach(({ start, end }) => {
    if (start instanceof Date && !Number.isNaN(start.getTime())) {
      min = Math.min(min, clamp(start.getHours()));
    }
    if (end instanceof Date && !Number.isNaN(end.getTime())) {
      const endHour = end.getMinutes() > 0 ? end.getHours() + 1 : end.getHours();
      max = Math.max(max, clamp(endHour));
    }
  });

  return { slotMinTime: asClock(min), slotMaxTime: max >= 24 ? '24:00:00' : asClock(max) };
}
